import { IconChevronDown } from '@developerz/ui';
import {
  createEffect,
  createMemo,
  createSignal,
  createUniqueId,
  For,
  type JSX,
  on,
  onCleanup,
  onMount,
  Show,
} from 'solid-js';
import { t } from '../i18n';
import { isListboxKey, nextListboxIndex } from '../lib/listbox-keys';
import { createNarrowViewport } from '../lib/viewport';
import { ActionDrawer } from './ActionDrawer';
import type { FieldA11y } from './Field';
import { Popover } from './Popover';
import { SelectOptionRow } from './SelectOptionRow';

// The themed, DOM-rendered dropdown (epic #5620 slice 02) that replaces a raw
// `<select>` everywhere this app offers a choice from a short list — the Agents
// page's four native selects first, every other one across 07a-d. Reuses, rather
// than copies (repo one-impl rule, `scripts/one-impl.test.ts`):
//   - `Popover.tsx` for the desktop anchored panel (its extended trigger props,
//     added alongside this file, let the trigger be named by an external
//     `label[for]` instead of `aria-label`);
//   - `ActionDrawer side="bottom"` + `lib/viewport.ts` for the phone sheet, the
//     exact switch `ModelPicker.tsx` already makes;
//   - `lib/listbox-keys.ts` for the ↑ ↓ Home End step, shared with
//     `ModelPickerList` (which this slice also switches onto it).
//
// The control itself is a `<button>` (labelable, so `label[for=id]` still names
// it and `getByLabelText` still finds it) rather than a native `<select>`. The
// open panel's `role="listbox"` is THIS file's own element — not a sibling's —
// which is the live-test fence's needle and the acceptance criterion's own
// wording; `SelectOptionRow` paints one row, never the listbox container.
//
// KEYBOARD: ≤8 options, no visible filter — the listbox itself is the focus
// target (`tabIndex=-1`) and typing is TYPE-AHEAD (jumps to the first label
// starting with the accumulated buffer, cleared 500ms after the last
// keystroke). Over 8, a visible filter input owns the combobox role and
// `aria-activedescendant` instead (`ModelPickerList`'s own shape: substring,
// case-insensitive). Either way `onPick` is only ever called with a NEW value —
// a pick of the value already selected is a no-op, the same as a native
// `<select>`, which never fires `change` for an unchanged pick.
//
// The whole open panel is mounted fresh every open (the Popover's/drawer's own
// `<Show>`), so its cursor/query/type-ahead buffer carry no state across opens.

export type SelectOption<V extends string> = {
  readonly value: V;
  readonly label: string;
  /** Secondary text beside the label (e.g. a model's provider). */
  readonly hint?: string;
  /** Consecutive options sharing a `group` render under one `role="group"`
   *  header; a run of `undefined` is the ungrouped head. */
  readonly group?: string;
  /** Excluded from the keyboard cursor and from a pointer pick. */
  readonly disabled?: boolean;
};

export type SelectProps<V extends string> = {
  readonly a11y: FieldA11y;
  /**
   * The already-translated `Field` label this control is paired with —
   * required, even though the DESKTOP trigger is named by that same label via
   * plain `label[for=id]` association (never read from here). On a phone, the
   * open panel is `ActionDrawer`'s own dialog, a DIFFERENT subtree the
   * `label[for]` association cannot reach, so this is its title — without it
   * every Select's phone sheet would fall back to the generic, identical
   * "Diff" (`ActionDrawer`'s own default), which cannot tell two different
   * fields' sheets apart (spec-gate note d).
   */
  readonly label: string;
  /**
   * `V` itself carries the "unset" sentinel when a call site needs one — e.g.
   * `RouteEffortField`'s `ReasoningEffort | ''`, where `''` is also a real,
   * listed option ("Model default"). Kept as plain `V` rather than this file
   * appending its own `| ''`: TS's generic inference decomposes a `V | ''`
   * PATTERN against an actual `SomeUnion | ''` argument by inferring
   * `V = SomeUnion` (stripping `''` back out), which would make `options:
   * SelectOption<V>[]` reject the very blank-valued option a caller like
   * `RouteEffortField` needs to list. A caller with no blank-sentinel need at
   * all (every current `V = string` site) is unaffected either way.
   */
  readonly value: V;
  readonly options: readonly SelectOption<V>[];
  readonly onChange: (value: V) => void;
  readonly disabled?: boolean;
  /** Shown on the trigger when {@link value} matches no option (never reached
   *  by this slice's 4 sites, each of which lists its own blank choice). */
  readonly placeholder?: string;
  /** Trigger is full-width, the `.select-trigger--block` look. */
  readonly block?: boolean;
  /** Renders a `<input type="hidden">` carrying {@link value}, so any `FormData`
   *  reader of the old `<select name>` keeps working. */
  readonly name?: string;
};

const FILTER_THRESHOLD = 8;
const TYPE_AHEAD_RESET_MS = 500;

/**
 * The trigger's text. `''` is checked against the options FIRST (a call site
 * can list it as a real, labelled choice — `RouteEffortField`'s "Model
 * default" — in which case it is an ordinary match, not the unset state) and
 * only falls back to `placeholder` when no option claims it. A stored value
 * that matches no option at all (ASSUMPTION, not exercised by this slice's 4
 * sites: a route pinned to a provider outside `ProviderSelect`'s customer-
 * addressable set) shows the raw value rather than going blank — some text
 * beats a trigger that reads as unset when a route genuinely has a pin.
 */
function triggerLabel<V extends string>(
  options: readonly SelectOption<V>[],
  value: V,
  placeholder: string | undefined,
): string | undefined {
  const found = options.find((o) => o.value === value);
  if (found !== undefined) return found.label;
  return value === '' ? placeholder : value;
}

/** One render group: `name` undefined is the ungrouped head. Group order follows
 *  each option's first appearance, never re-sorted. */
function groupRows<V extends string>(
  options: readonly SelectOption<V>[],
): readonly { readonly name: string | undefined; readonly rows: readonly SelectOption<V>[] }[] {
  const groups: { name: string | undefined; rows: SelectOption<V>[] }[] = [];
  for (const option of options) {
    const last = groups.at(-1);
    if (last !== undefined && last.name === option.group) last.rows.push(option);
    else groups.push({ name: option.group, rows: [option] });
  }
  return groups;
}

/** A row id stable for any `V` — mirrors `ModelPickerList.domId`'s sanitizer. */
function rowDomId(listId: string, value: string): string {
  return `${listId}-${value.replace(/[^\w-]/g, '_') || 'blank'}`;
}

/** Accumulates consecutive single-character keys into a lowercase buffer,
 *  cleared 500ms after the last one; `onType` is called with the buffer after
 *  every keystroke it accepts. */
function createTypeAhead(onType: (buffer: string) => void): (key: string) => void {
  let buffer = '';
  let timer: ReturnType<typeof setTimeout> | undefined;
  onCleanup(() => clearTimeout(timer));
  return (key: string) => {
    if (key.length !== 1) return;
    buffer += key.toLowerCase();
    clearTimeout(timer);
    timer = setTimeout(() => {
      buffer = '';
    }, TYPE_AHEAD_RESET_MS);
    onType(buffer);
  };
}

type ListboxProps<V extends string> = {
  readonly options: readonly SelectOption<V>[];
  readonly value: V;
  readonly sheet: boolean;
  /** `Field`'s own `${id}-label` (see `Field.tsx`'s comment on it) — the
   *  listbox's accessible name. NOT the trigger's id: an `aria-labelledby`
   *  pointing at the trigger fails when the trigger's own content is empty
   *  (an unset value), because the accname algorithm does not re-apply a
   *  REFERENCED node's host-language `<label for>` association, only its own
   *  content. Pointing at the label element itself sidesteps that. */
  readonly labelledBy: string;
  readonly onPick: (value: V) => void;
  /** Tab's own handler: ends the panel WITHOUT `preventDefault`, so the
   *  browser's own Tab still moves focus on from here — the same contract as
   *  Escape (owned by the Popover/sheet, not this file), just reached by a
   *  different key. */
  readonly onClose: () => void;
};

/** The open panel: a WAI-ARIA listbox (`ModelPickerList`'s own pattern) over
 *  the generic `SelectOption` shape. Mounted fresh every open. */
function SelectListbox<V extends string>(props: ListboxProps<V>): JSX.Element {
  const listId = createUniqueId();
  // `filterable` counts ALL options, disabled included — a disabled row still
  // occupies a slot a user scans past, so it still counts toward "more than 8
  // options needs a filter".
  const filterable = (): boolean => props.options.length > FILTER_THRESHOLD;
  const [query, setQuery] = createSignal('');
  // Query matching runs over every option, disabled included — a disabled row
  // is still a real answer to "where is X", it is just not PICKABLE.
  const filtered = createMemo(() => {
    const q = query().trim().toLowerCase();
    return filterable() && q !== ''
      ? props.options.filter((o) => o.label.toLowerCase().includes(q))
      : props.options;
  });
  // The keyboard cursor's own domain: disabled rows are excluded entirely, so
  // ↑ ↓ Home End and type-ahead can never land the cursor on one.
  const selectable = createMemo(() => filtered().filter((o) => o.disabled !== true));
  const [cursor, setCursor] = createSignal(
    Math.max(
      selectable().findIndex((o) => o.value === props.value),
      0,
    ),
  );
  let input: HTMLInputElement | undefined;
  let list: HTMLDivElement | undefined;

  createEffect(on(query, () => setCursor(0), { defer: true }));
  const active = (): number => Math.min(cursor(), Math.max(selectable().length - 1, 0));
  const activeRow = (): SelectOption<V> | undefined => selectable()[active()];
  const domId = (o: SelectOption<V>): string => rowDomId(listId, o.value);
  const activeDomId = (): string | undefined => {
    const row = activeRow();
    return row === undefined ? undefined : domId(row);
  };

  // Macrotask, not a microtask: `ModelPickerList`'s own reason — the sheet's
  // dialog trap focuses in a microtask, and the panel's own control is where
  // the keyboard contract starts.
  onMount(() => {
    const timer = setTimeout(() => (input ?? list)?.focus({ preventScroll: true }), 0);
    onCleanup(() => clearTimeout(timer));
  });

  createEffect(() => {
    const row = activeRow();
    if (row === undefined) return;
    document.getElementById(domId(row))?.scrollIntoView?.({ block: 'nearest' });
  });

  const typeAhead = createTypeAhead((buffer) => {
    const idx = selectable().findIndex((o) => o.label.toLowerCase().startsWith(buffer));
    if (idx !== -1) setCursor(idx);
  });

  const pickActive = (event: KeyboardEvent): void => {
    event.preventDefault();
    const row = activeRow();
    if (row !== undefined) props.onPick(row.value);
  };

  const onKeyDown = (event: KeyboardEvent): void => {
    // No `preventDefault`: the browser's own Tab still moves focus on.
    if (event.key === 'Tab') {
      props.onClose();
      return;
    }
    if (isListboxKey(event.key)) {
      event.preventDefault();
      const total = selectable().length;
      if (total > 0) setCursor(nextListboxIndex(active(), total, event.key));
      return;
    }
    if (event.key === 'Enter') {
      pickActive(event);
      return;
    }
    // Space picks only in the plain-listbox form (no visible filter): in the
    // filter input, Space is an ordinary typed character, same as any other
    // key the branch below does not intercept (filterable() guards it out of
    // the type-ahead buffer, and no `preventDefault` here lets the browser
    // insert it).
    if (event.key === ' ' && !filterable()) {
      pickActive(event);
      return;
    }
    // Escape is deliberately NOT handled here: the Popover's (or the sheet's)
    // own listener owns it, same contract as `ModelPickerList`.
    if (!filterable() && !event.ctrlKey && !event.metaKey && !event.altKey) {
      typeAhead(event.key);
    }
  };

  const row = (o: SelectOption<V>): JSX.Element => (
    <SelectOptionRow
      option={o}
      domId={domId(o)}
      active={activeRow()?.value === o.value}
      current={o.value === props.value}
      onPick={o.disabled === true ? undefined : () => props.onPick(o.value)}
    />
  );

  return (
    <div class="select__panel">
      <Show when={filterable()}>
        <input
          ref={input}
          type="text"
          class="select__filter"
          role="combobox"
          aria-expanded={true}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={activeDomId()}
          aria-label={t('select.filter_label')}
          placeholder={t('select.filter_label')}
          value={query()}
          onInput={(e) => setQuery(e.currentTarget.value)}
          onKeyDown={onKeyDown}
        />
      </Show>
      <div
        ref={list}
        id={listId}
        class={`select__list ${props.sheet ? 'select__list--sheet' : ''}`}
        role="listbox"
        tabIndex={filterable() ? undefined : -1}
        aria-activedescendant={filterable() ? undefined : activeDomId()}
        aria-labelledby={props.labelledBy}
        onKeyDown={filterable() ? undefined : onKeyDown}
      >
        <For each={groupRows(filtered())}>
          {(group) => (
            <Show when={group.name !== undefined} fallback={<For each={group.rows}>{row}</For>}>
              {/* biome-ignore lint/a11y/useSemanticElements: a listbox-owned group, not a form fieldset — ModelPickerList's own ruling. */}
              <div class="select__group" role="group" aria-label={group.name}>
                <div class="select__group-label" aria-hidden="true">
                  {group.name}
                </div>
                <For each={group.rows}>{row}</For>
              </div>
            </Show>
          )}
        </For>
        <Show when={filtered().length === 0}>
          <p class="select__empty">{t('select.no_match')}</p>
        </Show>
      </div>
    </div>
  );
}

export function Select<V extends string>(props: SelectProps<V>): JSX.Element {
  const narrow = createNarrowViewport();
  const [sheetOpen, setSheetOpen] = createSignal(false);
  const label = createMemo(() => triggerLabel(props.options, props.value, props.placeholder));
  const triggerClass = (): string =>
    `select-trigger select__trigger ${props.block === true ? 'select-trigger--block' : ''}`.trim();

  // A pick of the value already selected is a no-op (see file header).
  const pick = (close: () => void, next: V): void => {
    close();
    if (next !== props.value) props.onChange(next);
  };

  // The chevron's rotate-on-open is pure CSS, keyed off the trigger's own
  // `aria-expanded` (`[aria-expanded='true'] .select__chevron`, mirroring
  // `.model-picker__chevron`'s own rule) — not a Solid signal here, because
  // the desktop branch's `Popover` owns its `open` state internally and
  // never hands it back to a caller.
  const face = (): JSX.Element => (
    <>
      <span class="select__value">{label()}</span>
      <IconChevronDown class="select__chevron" size="0.875rem" />
    </>
  );

  const panel = (close: () => void, sheet: boolean): JSX.Element => (
    <SelectListbox
      options={props.options}
      value={props.value}
      sheet={sheet}
      labelledBy={`${props.a11y.id}-label`}
      onPick={(v) => pick(close, v)}
      onClose={close}
    />
  );

  return (
    <>
      <Show
        when={narrow()}
        fallback={
          <Popover
            trigger={face()}
            namedByField
            triggerId={props.a11y.id}
            triggerDescribedBy={props.a11y['aria-describedby']}
            triggerInvalid={props.a11y['aria-invalid']}
            triggerClass={triggerClass()}
            panelClass="select__popover"
            triggerDisabled={props.disabled}
          >
            {(close) => panel(close, false)}
          </Popover>
        }
      >
        <button
          type="button"
          id={props.a11y.id}
          aria-describedby={props.a11y['aria-describedby']}
          aria-invalid={props.a11y['aria-invalid']}
          class={triggerClass()}
          aria-haspopup="dialog"
          aria-expanded={sheetOpen()}
          disabled={props.disabled}
          onClick={() => setSheetOpen(true)}
        >
          {face()}
        </button>
        <ActionDrawer
          open={sheetOpen()}
          side="bottom"
          title={props.label}
          onClose={() => setSheetOpen(false)}
        >
          <Show when={sheetOpen()}>{panel(() => setSheetOpen(false), true)}</Show>
        </ActionDrawer>
      </Show>
      <Show when={props.name}>
        {(name) => <input type="hidden" name={name()} value={props.value} />}
      </Show>
    </>
  );
}
